import { Component, OnInit, signal } from '@angular/core';
import { ModalService } from '../../../core/services/modal.service';
import { PrivateBookingModalComponent } from '../../components/private-booking-modal/private-booking-modal.component';
import { Store } from '@ngrx/store';
import { roomsActions } from '../../../core/store/rooms';
import { BookingPermission } from 'angular/app/core/models/entities/permission';
import { bookingsActions } from 'angular/app/core/store/bookings';
import { ActivatedRoute, Router } from '@angular/router';
import { fromStore } from 'angular/app/core/store';
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs';
import { User } from '../../../core/models/entities/user';
import { communicationTemplatesActions } from 'angular/app/core/store/communication-templates';
import {
  BookingsDisplayMode,
  BookingsScope,
} from '../../../core/store/bookings/bookings.state';
import { parseISO } from 'date-fns';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { PersonalBookingsModalComponent } from '../../components/personal-bookings-modal/personal-bookings-modal.component';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import {
  selectAuthUser,
  selectFocusedBooking,
} from '../../../core/store/reducers';
import { ExportExcelModalComponent } from '../../components/export-excel-modal/export-excel-modal.component';

@Component({
  selector: 'app-bookings-page',
  template: `
    <app-page-header title="Réservations">
      <!-- Actions template -->
      <ng-template #actions>
        @if (hasPrivateRoom$ | async) {
          <app-button variant="outlined" (click)="openMyBookingsModal()">
            Mes réservations
          </app-button>
        }

        <ng-template [appHasPermission]="BookingPermission.Export">
          <app-button
            variant="outlined"
            [loading]="exportingData()"
            (click)="openExportExcelModal()"
            >Exporter</app-button
          >
        </ng-template>

        <ng-template
          [appHasPermission]="BookingPermission.StorePublic"
          #createBookingDropdown
        >
          @if (
            (hasPrivateRoom$ | async) === true ||
            (hasPublicRoom$ | async) === true
          ) {
            <app-dropdown>
              <app-button dropdownButton id="add-booking"
                >Ajouter une réservation</app-button
              >
              @if (hasPrivateRoom$ | async) {
                <button
                  dropdownAction
                  type="button"
                  data-cy="internal-booking-action"
                  (click)="createPrivateBooking()"
                >
                  Interne
                </button>
              }
              @if (hasPublicRoom$ | async) {
                <button
                  dropdownAction
                  type="button"
                  data-cy="external-booking-action"
                  (click)="createPublicBooking()"
                >
                  Externe
                </button>
              }
            </app-dropdown>
          }
        </ng-template>
        <ng-template [appHasNoPermission]="BookingPermission.StorePublic">
          <app-button id="add-private-booking" (click)="createPrivateBooking()">
            Ajouter
          </app-button>
        </ng-template>
      </ng-template>
    </app-page-header>

    <app-spacer></app-spacer>

    <ng-template [appHasPermission]="BookingPermission.Update">
      <app-pending-bookings></app-pending-bookings>
    </ng-template>

    <app-bookings-list [scope]="BookingsScope.All"></app-bookings-list>
  `,
  standalone: false,
})
export class BookingsPageComponent
  extends DestroyableComponent
  implements OnInit
{
  BookingPermission = BookingPermission;
  BookingsScope = BookingsScope;
  hasPublicRoom$ = this.store
    .select(fromStore.selectAllRooms)
    .pipe(
      map((rooms) =>
        rooms.some(
          (room) => room.active && room.bookable_by !== BookableBy.Private,
        ),
      ),
    );
  hasPrivateRoom$ = this.store
    .select(fromStore.selectAllRooms)
    .pipe(
      map((rooms) =>
        rooms.some(
          (room) => room.active && room.bookable_by !== BookableBy.Public,
        ),
      ),
    );
  loggedUser$ = this.store.select(selectAuthUser);
  exportingData = signal(false);

  constructor(
    private modalService: ModalService,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loggedUser$
      .pipe(
        takeUntil(this.onDestroy$),
        distinctUntilChanged((a, b) => a?.vendor?.id === b?.vendor?.id),
      )
      .subscribe((user) => {
        this.store.dispatch(roomsActions.findAll({ page: 1 }));
        this.store.dispatch(communicationTemplatesActions.findAll());
      });

    if (this.route.snapshot.queryParams['date']) {
      this.store.dispatch(
        bookingsActions.setDisplayMode({
          displayMode: BookingsDisplayMode.Week,
          scope: BookingsScope.All,
        }),
      );
      this.store.dispatch(
        bookingsActions.setCalendarDate({
          date: parseISO(this.route.snapshot.queryParams['date']),
          scope: BookingsScope.All,
        }),
      );
    }
  }

  createPublicBooking() {
    this.store
      .select(fromStore.selectAuthUser)
      .pipe(take(1))
      .subscribe((user: User) => {
        this.router.navigate(['/louer-salle/', user.vendor.slug]);
      });
  }

  createPrivateBooking() {
    const instance = this.modalService.open(PrivateBookingModalComponent, {
      title: 'Ajouter une réservation interne',
      size: 'screen',
    });
    (
      instance.componentRef.instance as PrivateBookingModalComponent
    ).bookingSaved.subscribe(() => {
      this.store.dispatch(
        bookingsActions.setDisplayMode({
          displayMode: BookingsDisplayMode.Week,
          scope: BookingsScope.All,
        }),
      );

      this.store.dispatch(
        bookingsActions.setCalendarDate({
          date: parseISO(this.store.selectSignal(selectFocusedBooking)().start),
          scope: BookingsScope.All,
        }),
      );
    });
  }

  openMyBookingsModal() {
    const instance = this.modalService.open(PersonalBookingsModalComponent, {
      size: 'screen',
      title: 'Voir mes réservations internes',
      showActionButtons: false,
    });
  }

  openExportExcelModal() {
    const modalRef = this.modalService.open(ExportExcelModalComponent, {});
  }
}
